import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select-legacy';
import { Link } from 'react-router-dom';
import { isEmpty, join, split, trim } from 'lodash';
import { linkWithParams } from 'routes';
import { formatMapIds, formatMapNames } from 'utils/Formatters';
import { withTranslation } from 'react-i18next';
import i18n from '../../utils/i18n';

class AccountPlayerDetails extends React.Component {
  static propTypes = {
    accountPlayer: PropTypes.object.isRequired,
    cohorts: PropTypes.arrayOf(PropTypes.object).isRequired,
    editable: PropTypes.bool.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onResetCohorts: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = { isEdit: false, ...this.initialState() };
  }

  initialState() {
    const { firstName, lastName, cohorts, customIds } = this.props.accountPlayer;
    const customIdValues = {};
    if (customIds) {
      customIds.forEach(cid => {
        customIdValues[cid.handle] = cid.fieldType === 'checkbox' ? cid.value === 'Yes' || cid.value === 'true' : (cid.value || '');
      });
    }
    return { firstName, lastName, cohortIds: formatMapIds(cohorts), customIdValues };
  }

  onCancel = () => {
    this.setState({ isEdit: false });
  }

  onChange = (e) => {
    const target = e.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    this.setState({ [name]: value });
  }

  onCustomIdChange = (handle, fieldType) => (e) => {
    const value = fieldType === 'checkbox' ? e.target.checked : e.target.value;
    this.setState(prevState => ({
      customIdValues: { ...prevState.customIdValues, [handle]: value }
    }));
  }

  onCohortChange = (cohortIds) => {
    this.setState({ cohortIds });
  }

  onEdit = () => {
    this.setState({ isEdit: true, ...this.initialState() });
  }

  onUpdate = () => {
    const { accountPlayer } = this.props;
    const { firstName, lastName, cohortIds, customIdValues } = this.state;
    this.setState({ isEdit: false });

    const customIds = accountPlayer.customIds ? accountPlayer.customIds.map(cid => ({
      handle: cid.handle,
      value: cid.fieldType === 'checkbox' ? String(customIdValues[cid.handle] || false) : (customIdValues[cid.handle] || '')
    })) : [];

    this.props.onUpdate(accountPlayer.id, { firstName, lastName, cohortIds: split(cohortIds, ','), customIds });
  }

  onResetCohorts = () => {
    if (window.confirm(i18n.t('confirm.removeCohort'))) {
      const { accountPlayer } = this.props;
      this.setState({ isEdit: false });
      this.props.onResetCohorts(accountPlayer.id);
    }
  }

  render() {
    const { t } = this.props;
    const { isEdit, firstName, lastName, cohortIds } = this.state;
    const { accountPlayer, cohorts, editable } = this.props;
    const clsUpdate = classNames('o-sp-button o-sp-button--update o-sp-button--primary',
      { 'u-disabled': isEmpty(trim(firstName)) || isEmpty(trim(lastName)) || isEmpty(trim(cohortIds)) }
    );

    const careGivers = accountPlayer.careGivers.map(c => (
      <li key={`link-${c.value}`}>
        <Link className='cs-txt-link--sp-blue' to={{ pathname: linkWithParams('account', { id: c.value }) }}>
          {c.label}
        </Link>
      </li>
    ));
    const parentLink = (
      <Link key={`link-${accountPlayer.creator.id}`} className='cs-txt-link--sp-blue' to={{ pathname: linkWithParams('account', { id: accountPlayer.creator.id }) }}>
        {accountPlayer.creator.name} (main)
      </Link>
    )

    return (
      <section className='column o-card cs-bg--light'>
        {!isEdit && editable && <a className='cs-txt-link--sp-blue u-float--right' onClick={this.onEdit}>{t('accountPlayer.edit')}</a>}
        {isEdit ? (
          <p className='u-margin-bottom--tiny'>
            <span className='u-txt--label'>{t('accountPlayer.birthday')}</span><br />
            <input name='birthday' type='text' value={accountPlayer.birthday} disabled />
          </p>
        ) : (
          <p className='u-margin-bottom--tiny'>
            <span className='u-txt--label'>{t('accountPlayer.birthday')}</span><br />
            {accountPlayer.birthday}
          </p>
        )}
        { accountPlayer.weeksPremature > 0 && (
          <>
            {isEdit && accountPlayer.weeksPremature > 0 ? (
              <p className='u-margin-bottom--tiny'>
                <span className='u-txt--label'>{t('accountPlayer.weeksPremature')}</span><br />
                <input name='weeksPremature' type='text' value={accountPlayer.weeksPremature} disabled />
              </p>
            ) : (
              <p className='u-margin-bottom--tiny'>
                <span className='u-txt--label'>{t('accountPlayer.weeksPremature')}</span><br />
                {accountPlayer.weeksPremature}
              </p>
            )}
          </>
        )}
        {isEdit &&
          <p className='u-margin-bottom--tiny'>
            <span className='u-txt--label'>{t('accountPlayer.firstName')}</span><br />
            <input name='firstName' type='text' value={firstName} onChange={this.onChange} />
          </p>}
        {isEdit &&
          <p className='u-margin-bottom--tiny'>
            <span className='u-txt--label'>{t('accountPlayer.lastName')}</span><br />
            <input name='lastName' type='text' value={lastName} onChange={this.onChange} />
          </p>}
        <ul className='no-bullet u-margin-bottom--tiny'>
          <li><span className='u-txt--label'>{t('accountPlayer.caregivers')}</span></li>
          <li>{parentLink}</li>
          {careGivers}
        </ul>
        <p className='u-margin-bottom--tiny'>
          <span className='u-txt--label'>{t('accountPlayer.teacher')}</span><br />
          {isEmpty(accountPlayer.teachers) ? t('accountPlayer.none') : join(accountPlayer.teachers, ', ')}
        </p>
        <p className='u-margin-bottom--tiny'>
          <span className='u-txt--label'>{t('accountPlayer.groupName')}</span><br />
          {join(accountPlayer.groups, ', ')}
        </p>
        <p className='u-margin-bottom--tiny'>
          <span className='u-txt--label'>{t('accountPlayer.subgroupName')}</span><br />
          {join(accountPlayer.subgroups, ', ')}
        </p>
        <div className='u-margin-bottom--zero'>
          <span className='u-txt--label'>{t('accountPlayer.cohort')}</span><br />
          {isEdit ?
            <Select
              name='sp-cohort-select'
              className={'o-select o-select--optcohort'}
              value={cohortIds || ''}
              multi={true}
              onChange={this.onCohortChange}
              options={cohorts}
              placeholder={t('accountPlayer.selectCohort')}
              simpleValue={true} /> : formatMapNames(accountPlayer.cohorts)}
        </div>
        {accountPlayer.customIds && accountPlayer.customIds.map(cid => (
          <p key={cid.handle} className='u-margin-bottom--tiny'>
            <span className='u-txt--label'>{cid.label}</span><br />
            {isEdit ? (
              cid.fieldType === 'checkbox' ? (
                <label>
                  <input
                    type='checkbox'
                    checked={!!this.state.customIdValues[cid.handle]}
                    onChange={this.onCustomIdChange(cid.handle, 'checkbox')}
                  />
                  {' '}{t('accountPlayer.yes')}
                </label>
              ) : (
                <input
                  type='text'
                  value={this.state.customIdValues[cid.handle] || ''}
                  onChange={this.onCustomIdChange(cid.handle, 'text')}
                />
              )
            ) : (
              cid.value || '-'
            )}
          </p>
        ))}
        <ul className='no-bullet u-margin-bottom--tiny'>
          <li><span className='u-txt--label'>{t('accountPlayer.siblings')}</span></li>
          {accountPlayer.siblings && accountPlayer.siblings.length > 0
            ? accountPlayer.siblings.map(s => (
              <li key={`sibling-${s.id}`}>
                <Link className='cs-txt-link--sp-blue' to={{ pathname: linkWithParams('accountPlayer', { id: s.id }) }}>
                  {s.name}
                </Link>
              </li>
            ))
            : <li>{t('accountPlayer.none')}</li>
          }
        </ul>
        {isEdit &&
          <div className='row align-middle u-margin-top--small'>
            <div className='column'>
              <a className={clsUpdate} onClick={this.onUpdate}>{t('accountPlayer.update')}</a>
              {this.props.accountPlayer.partnerDefaultGroup &&
                <a className="o-sp-button o-sp-button--update o-sp-button--danger u-margin-top--small" onClick={this.onResetCohorts}>{t('accountPlayer.remove')}</a>
              }
            </div>
            <div className='column'>
              <a className='o-sp-button--update cs-txt-link--sp-blue' onClick={this.onCancel}>{t('accountPlayer.cancel')}</a>
            </div>
          </div>}
      </section>
    )
  }
}

export default withTranslation()(AccountPlayerDetails);
